import type {
  TestData,
  TestAttempt,
  TestScores,
  SectionScore,
  PartScore,
  ScoringConfig,
  NormData,
  NormCurve,
  Section,
  Part,
  ChoiceId
} from '../types/testy';

/**
 * ScoringEngine handles all scoring calculations for ZSV tests
 * Implements the official ZSV scoring system: +1 correct, -1/3 wrong, 0 blank
 */
export class ScoringEngine {
  /**
   * Calculate score for a single question
   * @param userAnswer - The answer selected by the user (or undefined for blank)
   * @param correctAnswer - The correct answer for the question
   * @param scoring - Scoring configuration from test data
   * @returns Points earned for this question
   */
  calculateQuestionScore(
    userAnswer: ChoiceId | undefined,
    correctAnswer: ChoiceId,
    scoring: ScoringConfig
  ): number {
    if (!userAnswer) {
      return scoring.blank; // 0 points for blank
    }
    
    if (userAnswer === correctAnswer) {
      return scoring.correct; // +1 point for correct
    }
    
    return scoring.wrong; // -1/3 point for wrong
  }

  /**
   * Calculate scores for a single part within a section
   * @param part - Part data with questions
   * @param answers - User's answers (questionId -> choice)
   * @param scoring - Scoring configuration
   * @returns PartScore with detailed statistics
   */
  calculatePartScore(
    part: Part,
    answers: Record<string, ChoiceId>,
    scoring: ScoringConfig
  ): PartScore {
    let points = 0;
    let correct = 0;
    let wrong = 0;
    let blank = 0;
    const maxPoints = part.questions.length * scoring.correct;

    for (const question of part.questions) {
      const userAnswer = answers[question.id];
      const questionScore = this.calculateQuestionScore(userAnswer, question.correct, scoring);
      
      points += questionScore;
      
      if (!userAnswer) {
        blank++;
      } else if (userAnswer === question.correct) {
        correct++;
      } else {
        wrong++;
      }
    }

    return {
      partId: part.id,
      points,
      maxPoints,
      correct,
      wrong,
      blank
    };
  }

  /**
   * Calculate scores for a complete section
   * @param section - Section data with parts
   * @param answers - User's answers (questionId -> choice)
   * @param scoring - Scoring configuration
   * @param norms - Norm data for percentile calculation
   * @returns SectionScore with percentile if available
   */
  calculateSectionScore(
    section: Section,
    answers: Record<string, ChoiceId>,
    scoring: ScoringConfig,
    norms: NormData
  ): SectionScore {
    const parts: PartScore[] = [];
    let totalPoints = 0;
    let maxPoints = 0;

    // Calculate scores for each part
    for (const part of section.parts) {
      const partScore = this.calculatePartScore(part, answers, scoring);
      parts.push(partScore);
      totalPoints += partScore.points;
      maxPoints += partScore.maxPoints;
    }

    const percentage = maxPoints > 0 ? (totalPoints / maxPoints) * 100 : 0;
    
    // Calculate percentile if norm curve is available
    const sectionNorm = norms.sections[section.id];
    const percentile = sectionNorm ? this.calculatePercentile(totalPoints, sectionNorm) : undefined;

    return {
      sectionId: section.id,
      points: totalPoints,
      maxPoints,
      percentage,
      percentile,
      parts
    };
  }

  /**
   * Calculate percentile using piecewise-linear interpolation
   * @param score - The score to find percentile for
   * @param normCurve - Norm curve data with points and percentiles
   * @returns Percentile (0-100) or undefined if cannot be calculated
   */
  calculatePercentile(score: number, normCurve: NormCurve): number | undefined {
    const { points, percentiles } = normCurve;
    
    if (!points || !percentiles) {
      return undefined;
    }
    
    if (points.length !== percentiles.length || points.length < 2) {
      return undefined;
    }

    // Handle edge cases
    if (score <= points[0]) {
      return percentiles[0];
    }
    if (score >= points[points.length - 1]) {
      return percentiles[percentiles.length - 1];
    }

    // Find the two points to interpolate between
    for (let i = 0; i < points.length - 1; i++) {
      if (score >= points[i] && score <= points[i + 1]) {
        // Piecewise-linear interpolation
        const x1 = points[i];
        const x2 = points[i + 1];
        const y1 = percentiles[i];
        const y2 = percentiles[i + 1];
        
        // Linear interpolation formula: y = y1 + (y2 - y1) * (x - x1) / (x2 - x1)
        const percentile = y1 + (y2 - y1) * (score - x1) / (x2 - x1);
        
        // Clamp to valid percentile range
        return Math.max(0, Math.min(100, percentile));
      }
    }

    return undefined;
  }

  /**
   * Calculate estimated overall percentile as average of section percentiles
   * Used when overall norm curve is not available
   * @param sectionScores - Array of section scores with percentiles
   * @returns Estimated percentile or undefined if no section percentiles available
   */
  calculateEstimatedPercentile(sectionScores: SectionScore[]): number | undefined {
    const validPercentiles = sectionScores
      .map(section => section.percentile)
      .filter((percentile): percentile is number => percentile !== undefined);

    if (validPercentiles.length === 0) {
      return undefined;
    }

    const sum = validPercentiles.reduce((acc, percentile) => acc + percentile, 0);
    return sum / validPercentiles.length;
  }

  /**
   * Calculate complete test scores including all sections and overall metrics
   * @param testData - Complete test data
   * @param attempt - User's test attempt with answers
   * @returns Complete TestScores object
   */
  calculateTestScores(testData: TestData, attempt: TestAttempt): TestScores {
    const sectionScores: SectionScore[] = [];
    let totalPoints = 0;
    let maxTotalPoints = 0;

    // Calculate scores for each section
    for (const section of testData.sections) {
      const sectionScore = this.calculateSectionScore(
        section,
        attempt.answers,
        testData.scoring,
        testData.norms
      );
      
      sectionScores.push(sectionScore);
      totalPoints += sectionScore.points;
      maxTotalPoints += sectionScore.maxPoints;
    }

    const totalPercentage = maxTotalPoints > 0 ? (totalPoints / maxTotalPoints) * 100 : 0;

    // Calculate overall percentile
    let overallPercentile: number | undefined;
    let estimatedPercentile: number | undefined;

    if (testData.norms.overall) {
      overallPercentile = this.calculatePercentile(totalPoints, testData.norms.overall);
    } else {
      estimatedPercentile = this.calculateEstimatedPercentile(sectionScores);
    }

    return {
      totalPoints,
      maxTotalPoints,
      totalPercentage,
      overallPercentile,
      estimatedPercentile,
      sections: sectionScores
    };
  }

  /**
   * Validate that answers match the test structure
   * @param testData - Test data to validate against
   * @param answers - User answers to validate
   * @returns Array of validation errors (empty if valid)
   */
  validateAnswers(testData: TestData, answers: Record<string, ChoiceId>): string[] {
    const errors: string[] = [];
    const validQuestionIds = new Set<string>();

    // Collect all valid question IDs
    for (const section of testData.sections) {
      for (const part of section.parts) {
        for (const question of part.questions) {
          validQuestionIds.add(question.id);
        }
      }
    }

    // Check for invalid question IDs in answers
    for (const questionId of Object.keys(answers)) {
      if (!validQuestionIds.has(questionId)) {
        errors.push(`Invalid question ID: ${questionId}`);
      }
    }

    // Check for invalid choice values
    const validChoices = new Set<ChoiceId>(['A', 'B', 'C', 'D']);
    for (const [questionId, choice] of Object.entries(answers)) {
      if (!validChoices.has(choice)) {
        errors.push(`Invalid choice '${choice}' for question ${questionId}`);
      }
    }

    return errors;
  }

  /**
   * Get summary statistics for a completed test
   * @param scores - Test scores to summarize
   * @returns Summary object with key metrics
   */
  getScoreSummary(scores: TestScores) {
    const totalQuestions = scores.sections.reduce(
      (sum, section) => sum + section.parts.reduce(
        (partSum, part) => partSum + part.correct + part.wrong + part.blank, 0
      ), 0
    );

    const totalCorrect = scores.sections.reduce(
      (sum, section) => sum + section.parts.reduce(
        (partSum, part) => partSum + part.correct, 0
      ), 0
    );

    const totalWrong = scores.sections.reduce(
      (sum, section) => sum + section.parts.reduce(
        (partSum, part) => partSum + part.wrong, 0
      ), 0
    );

    const totalBlank = scores.sections.reduce(
      (sum, section) => sum + section.parts.reduce(
        (partSum, part) => partSum + part.blank, 0
      ), 0
    );

    return {
      totalQuestions,
      totalCorrect,
      totalWrong,
      totalBlank,
      answeredQuestions: totalCorrect + totalWrong,
      accuracyRate: totalCorrect + totalWrong > 0 ? (totalCorrect / (totalCorrect + totalWrong)) * 100 : 0,
      completionRate: (totalCorrect + totalWrong) / totalQuestions * 100,
      primaryPercentile: scores.overallPercentile ?? scores.estimatedPercentile,
      hasOfficialPercentile: scores.overallPercentile !== undefined
    };
  }
}

// Export singleton instance
export const scoringEngine = new ScoringEngine();